'use client'

import { type FeatureFlag, useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  FeatureBadge,
  type FeatureBadgeEntry,
  useFeatureBadge,
} from '@b-stock/fe-core/bstock-react'

import { featureBadgeEventProps } from '@/helpers/featureBadges/analytics'
import {
  FEATURE_BADGES,
  type FeatureBadgeKey,
} from '@/helpers/featureBadges/constants'
import { useReportPageFeatureBadge } from '@/helpers/featureBadges/pageFeatureBadges'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

export type FeatureBadgeSlotProps = {
  featureKey: FeatureBadgeKey
}

const NewBadge = ({
  featureKey,
  entry,
}: {
  featureKey: FeatureBadgeKey
  entry: FeatureBadgeEntry
}) => {
  const resolution = useFeatureBadge(entry)
  useReportPageFeatureBadge(featureKey, resolution.active)

  return resolution.active ? <FeatureBadge variant={resolution.kind} /> : null
}

// The only place a Beta badge's flag is read: the read is the LD exposure
const BetaBadge = ({
  featureKey,
  flag,
}: {
  featureKey: FeatureBadgeKey
  flag: FeatureFlag<boolean>
}) => {
  // useFlag casts without checking: a mistyped variation must mean no badge
  const flagValue: unknown = useFlag(flag)
  const shown = flagValue === true
  useReportPageFeatureBadge(featureKey, shown)
  const { trackButtonClicked } = useAnalytics()

  if (!shown) return null
  return (
    <FeatureBadge
      variant="beta"
      onExplainerOpen={() =>
        trackButtonClicked({
          buttonName: 'feature_badge_explainer',
          ...featureBadgeEventProps(featureKey, { pillShown: true }),
        })
      }
    />
  )
}

const FeatureBadgeSlot = ({ featureKey }: FeatureBadgeSlotProps) => {
  const entry = FEATURE_BADGES[featureKey]
  return entry.kind === 'beta' ? (
    <BetaBadge featureKey={featureKey} flag={entry.flag} />
  ) : (
    <NewBadge featureKey={featureKey} entry={entry} />
  )
}

export default FeatureBadgeSlot
